import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLanguage } from '../contexts/LanguageContext';
import { formatPrice } from '../lib/utils';
import { api } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import { ArrowLeft, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { createPaymentFormSchema, type PaymentFormData } from '../lib/validations';
import type { Sale, Payment } from '../types/car';

export default function CarDetails() {
  const { t } = useLanguage();
  const { carId } = useParams<{ carId: string }>();
  const navigate = useNavigate();
  const saleId = carId; // URL still uses carId for backward compat
  const [loading, setLoading] = useState(true);
  const [sale, setSale] = useState<Sale | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [successMessage, setSuccessMessage] = useState('');

  const remainingBalance = sale ? sale.salePrice - payments.reduce((sum, p) => sum + p.amount, 0) : 0;
  const paymentFormSchema = createPaymentFormSchema(remainingBalance);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    clearErrors,
    watch,
    setValue,
  } = useForm<PaymentFormData>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: { amount: 0, notes: '' },
  });

  const watchedAmount = watch('amount') || 0;

  useEffect(() => {
    if (saleId) fetchSale();
  }, [saleId]);

  const fetchSale = async () => {
    if (!saleId) return;
    setLoading(true);
    try {
      const data = await api.get(`/api/sales/${saleId}`);
      setSale(data);
      setPayments(data.payments || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: PaymentFormData) => {
    if (!saleId) return;
    setSuccessMessage('');
    try {
      await api.post('/api/payments', {
        saleId: parseInt(saleId),
        amount: data.amount,
        currency: sale?.currency ?? 'USD',
        notes: data.notes || undefined,
      });
      setSuccessMessage(t('sellCar.paymentRecorded'));
      reset();
      fetchSale();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (e: any) {
      toast.error(e?.error ?? 'Failed to record payment');
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-48 rounded-lg" />
          <span className="text-sm text-slate-500">Loading…</span>
        </div>
      </div>
    );
  }

  if (!sale) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-slate-900 mb-4">{t('sellCar.carNotFound')}</h1>
          <Button onClick={() => navigate('/sell-car-history')} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 text-sm font-medium">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('sellCar.backToHistory')}
          </Button>
        </div>
      </div>
    );
  }

  const car = sale.warehouseCar;
  const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/sell-car-history')} className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('carDetails.backToHistory')}
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('carDetails.title')} & {t('carDetails.paymentHistory')}</h1>
              <p className="text-slate-500 text-sm mt-0.5">
                {car.brand.name} {car.model.name} • {car.year} • {car.color}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Card className="rounded-2xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('carDetails.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div>
                <Label className="text-sm font-medium text-slate-600">{t('carDetails.vehicle')}</Label>
                <p className="text-lg font-semibold"><span dir="ltr">{car.brand.name} {car.model.name}</span></p>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-600">{t('carDetails.year')}</Label>
                <p className="text-lg">{car.year}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-600">{t('carDetails.color')}</Label>
                <p className="text-lg">{car.color}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-600">{t('carDetails.salePrice')}</Label>
                <p className="text-lg font-semibold text-emerald-600">{formatPrice(sale.salePrice, sale.currency ?? 'USD')}</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-600">{t('carDetails.soldDate')}</Label>
                <p className="text-lg">{formatDate(sale.soldAt)}</p>
              </div>
            </div>
            {(car.chassisNo || car.plateNumber) && (
              <div className="mt-4 flex gap-6">
                {car.chassisNo && (
                  <div>
                    <Label className="text-sm font-medium text-slate-600">{t('carDetails.chassisNumber')}</Label>
                    <p className="text-sm font-mono" dir="ltr">{car.chassisNo}</p>
                  </div>
                )}
                {car.plateNumber && (
                  <div>
                    <Label className="text-sm font-medium text-slate-600">{t('carDetails.plateNumber')}</Label>
                    <p className="text-sm font-mono" dir="ltr">{car.plateNumber}</p>
                  </div>
                )}
              </div>
            )}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3 text-slate-900">{t('carDetails.sellerInformation')}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="font-medium text-slate-600">{t('carDetails.name')}:</span><span>{sale.sellerName || 'N/A'}</span></div>
                  <div className="flex justify-between"><span className="font-medium text-slate-600">{t('carDetails.contact')}:</span><span>{sale.sellerContact || 'N/A'}</span></div>
                  <div className="flex justify-between"><span className="font-medium text-slate-600">{t('carDetails.address')}:</span><span>{sale.sellerAddress || 'N/A'}</span></div>
                  <div className="flex justify-between"><span className="font-medium text-slate-600">{t('carDetails.idNo')}:</span><span className="font-mono">{sale.sellerIdNo || 'N/A'}</span></div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 text-slate-900">{t('carDetails.buyerInformation')}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="font-medium text-slate-600">{t('carDetails.name')}:</span><span>{sale.buyer?.name ?? 'N/A'}</span></div>
                  <div className="flex justify-between"><span className="font-medium text-slate-600">{t('carDetails.contact')}:</span><span>{sale.buyer?.contact ?? 'N/A'}</span></div>
                  <div className="flex justify-between"><span className="font-medium text-slate-600">{t('carDetails.address')}:</span><span>{sale.buyer?.address ?? 'N/A'}</span></div>
                  <div className="flex justify-between"><span className="font-medium text-slate-600">{t('carDetails.idNo')}:</span><span className="font-mono">{sale.buyer?.idNo ?? 'N/A'}</span></div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card className="rounded-2xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                <Plus className="h-5 w-5 mr-2" />
                {t('carDetails.addPayment')}
              </h2>
              {successMessage && (
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-sm">{successMessage}</div>
              )}
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <Label htmlFor="amount" className="mb-2 block">{t('carDetails.amount')}</Label>
                  <Input
                    id="amount"
                    type="number"
                    max={remainingBalance}
                    min={0}
                    step="any"
                    {...register('amount', { valueAsNumber: true, onChange: () => clearErrors('amount') })}
                    className={errors.amount ? 'border-red-500' : ''}
                  />
                  <p className="text-sm text-slate-500 mt-1">{t('carDetails.maxAmount')}: {formatPrice(remainingBalance, sale.currency ?? 'USD')}</p>
                  {errors.amount && <p className="text-red-600 text-sm mt-1">{errors.amount.message}</p>}
                </div>
                <div>
                  <Label htmlFor="notes" className="mb-2 block">{t('carDetails.notes')}</Label>
                  <Input id="notes" {...register('notes')} placeholder="e.g., Second payment" />
                </div>
                <Button type="button" variant="outline" className="w-full" disabled={remainingBalance <= 0} onClick={() => setValue('amount', remainingBalance)}>
                  {t('sellCar.payFullAmount')} ({formatPrice(remainingBalance, sale.currency ?? 'USD')})
                </Button>
                <Button type="submit" disabled={isSubmitting || watchedAmount > remainingBalance || watchedAmount <= 0} className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-4 py-2.5 text-sm font-medium disabled:opacity-50 disabled:pointer-events-none">
                  {isSubmitting ? t('carDetails.recording') : t('carDetails.recordPayment')}
                </Button>
              </form>
            </Card>
            <Card className="rounded-2xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('carDetails.paymentSummary')}</h2>
              <div className="space-y-4">
                <div className="flex justify-between"><span className="font-medium">{t('carDetails.totalSalePrice')}:</span><span className="font-semibold">{formatPrice(sale.salePrice, sale.currency ?? 'USD')}</span></div>
                <div className="flex justify-between"><span className="font-medium">{t('carDetails.totalPaid')}:</span><span className="font-semibold text-emerald-600 tabular-nums">{formatPrice(totalPaid, sale.currency ?? 'USD')}</span></div>
                <div className="flex justify-between border-t pt-2"><span className="font-medium">{t('carDetails.remainingBalance')}:</span><span className={`font-semibold tabular-nums ${remainingBalance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{formatPrice(remainingBalance, sale.currency ?? 'USD')}</span></div>
              </div>
            </Card>
          </div>
          <Card className="rounded-2xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('carDetails.paymentHistory')}</h2>
            <div className="space-y-3">
              {payments.length === 0 ? (
                <p className="text-slate-500 text-center py-4">{t('carDetails.noPayments')}</p>
              ) : (
                [...payments].sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()).map((p) => (
                  <div key={p.id} className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <p className="font-medium text-lg">{formatPrice(p.amount, p.currency ?? sale.currency ?? 'USD')}</p>
                    <p className="text-sm text-slate-600">{formatDate(p.paymentDate)}</p>
                    {p.notes && <p className="text-sm text-slate-500 mt-1">{p.notes}</p>}
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
